import { cn } from '@/lib/cn'
import { initials as toInitials } from '@/lib/format'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg'
export type AvatarTone = 'navy' | 'light' | 'gold'

export interface AvatarProps {
  /** Pełne imię i nazwisko — inicjały liczone automatycznie. */
  name: string | null | undefined
  size?: AvatarSize
  tone?: AvatarTone
  /** Nadpisuje inicjały (np. "AI" dla wpisów systemowych). */
  initials?: string
  title?: string
  className?: string
}

const SIZES: Record<AvatarSize, string> = {
  xs: 'h-[20px] w-[20px] text-[10px]',
  sm: 'h-[24px] w-[24px] text-[11px]',
  md: 'h-[28px] w-[28px] text-[11.5px]',
  lg: 'h-[34px] w-[34px] text-[13px]',
}

const TONES: Record<AvatarTone, string> = {
  navy: 'bg-navy-800 text-white',
  light: 'bg-[#dfe6eb] text-[#415a6b]',
  gold: 'bg-gold text-navy-900',
}

/** Kółko z inicjałami — zespół sprawy, autor aktywności, użytkownik w sidebarze. */
export function Avatar({ name, size = 'md', tone = 'navy', initials, title, className }: AvatarProps) {
  const text = initials ?? toInitials(name)
  return (
    <span
      title={title ?? name ?? undefined}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold uppercase tracking-wide',
        SIZES[size],
        TONES[tone],
        className,
      )}
    >
      {text}
    </span>
  )
}

export interface AvatarGroupProps {
  names: string[]
  size?: AvatarSize
  max?: number
  className?: string
}

/** Nakładające się avatary — kolumna ZESPÓŁ w rejestrze spraw. */
export function AvatarGroup({ names, size = 'sm', max = 4, className }: AvatarGroupProps) {
  const shown = names.slice(0, max)
  const rest = names.length - shown.length
  return (
    <div className={cn('flex items-center', className)}>
      {shown.map((name, index) => (
        <Avatar
          key={`${name}-${index}`}
          name={name}
          size={size}
          className={cn('ring-2 ring-card', index > 0 && '-ml-1.5')}
        />
      ))}
      {rest > 0 ? <span className="ml-1.5 text-micro text-muted">+{rest}</span> : null}
    </div>
  )
}
